import { memo, useMemo, useRef } from 'react';
import { Group, Text } from 'react-konva';
import { useKonvaImageScale } from 'contexts/ToolDimensionsAndScaleContext';
import { convertMetricToLinearFeet } from '../../../util';
import { useRefs } from 'contexts/RefContext';
import Konva from 'konva';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from 'store';
import { getPolygonArea, KonvaClassNames, KonvaGroupNames } from '../utils';
import useScreenDims from 'hooks/useScreenDims';
import { selectAllEdges } from 'store/slices/RoofDataSlice/roofSelector';

const EdgeLengthSolar = () => {
	const scale = useKonvaImageScale();
	const { groundMetersPerPixel, imgHeight, allRoofSegs } = useSelector((state: RootState) => {
		const roofData = state.roofData.data;
		return { groundMetersPerPixel: roofData.groundMetersPerPixel, imgHeight: roofData.imgHeight, allRoofSegs: roofData.allRoofSegs };
	}, shallowEqual);
	const { fullScreenEnabled, editModeEnabled, showLinearFtForSolar } = useSelector((state: RootState) => {
		const tool = state.toolNewPostions.data;
		return { fullScreenEnabled: tool.fullScreenEnabled, editModeEnabled: tool.editModeEnabled, showLinearFtForSolar: tool.showLinearFtForSolar };
	}, shallowEqual);
	// EdgeLengthText sizes its font from the live stage zoom, so re-render the labels when zoomLevel changes
	useSelector((state: RootState) => state.toolNewPostions.data.zoomLevel);
	// same edges as roof: in manual mode rakes, hips and valleys already carry the pitch adjusted length
	const allRoofEdges = useSelector(selectAllEdges);

	const transformedEdgeLengths = useMemo(() => (
		allRoofEdges.map((edge: EdgeData) => {
			const polygon = allRoofSegs.find(r => r.id === edge.roofSegmentId);

			const a = getPolygonArea(polygon?.simplifiedHullCoords?.flat() || []);
			const isCCW = a > 0;
			const baseOffset = !fullScreenEnabled
				? (imgHeight <= 601 ? 7 : 3)
				: (imgHeight <= 601 ? 3 : 2);
			const offsetInside = baseOffset / scale.x;

			const [[x1, y1], [x2, y2]] = edge.coords;
			const midX = (x1 + x2) / 2;
			const midY = (y1 + y2) / 2;

			let angleDeg = Math.atan2(y2 - y1, x2 - x1) * 180 / Math.PI;

			const dx = x2 - x1;
			const dy = y2 - y1;
			const length = Math.sqrt(dx * dx + dy * dy);

			// Interior normal
			let nx, ny;
			if (!isCCW) {
				nx = dy / length;
				ny = -dx / length;
			} else {
				nx = -dy / length;
				ny = dx / length;
			}

			// Flip upside-down labels
			if (angleDeg > 90 || angleDeg < -90) {
				angleDeg += 180;
			}

			// Move inside along the interior normal
			const posX = midX + nx * offsetInside;
			const posY = midY + ny * offsetInside;

			return ({
				id: edge.coordId,
				x: posX,
				y: posY,
				angle: angleDeg,
				text: convertMetricToLinearFeet(edge.length).toString(),
				// on-screen size of the drawn line, used only to hide labels on short edges
				pxLength: length * scale.x,
				name: KonvaClassNames.solarFeetLineText,
				visible: editModeEnabled && showLinearFtForSolar,
			});
		})), [allRoofEdges, allRoofSegs, editModeEnabled, fullScreenEnabled, imgHeight, scale.x, showLinearFtForSolar]);

	return (
		<Group
			scale={scale}
			listening={false}
			name={KonvaGroupNames.EDGE_LENGHT}
		>
			{transformedEdgeLengths.map((edge) => (
				<EdgeLengthText
					key={edge.id}
					edge={edge}
					fullScreenEnabled={fullScreenEnabled}
					imgHeight={imgHeight}
					groundMetersPerPixel={groundMetersPerPixel}
				/>
			))}
		</Group>
	);
};

const EdgeLengthRoof = () => {
	const scale = useKonvaImageScale();
	const { groundMetersPerPixel, imgHeight, allRoofSegs } = useSelector((state: RootState) => {
		const roofData = state.roofData.data;
		return { groundMetersPerPixel: roofData.groundMetersPerPixel, imgHeight: roofData.imgHeight, allRoofSegs: roofData.allRoofSegs };
	}, shallowEqual);
	const { fullScreenEnabled, threeDModelEnabled } = useSelector((state: RootState) => {
		const tool = state.toolNewPostions.data;
		return { fullScreenEnabled: tool.fullScreenEnabled, threeDModelEnabled: tool.threeDModelEnabled };
	}, shallowEqual);
	// EdgeLengthText sizes its font from the live stage zoom, so re-render the labels when zoomLevel changes
	useSelector((state: RootState) => state.toolNewPostions.data.zoomLevel);
	const enableEditFacet = useSelector((state: RootState) => state.roofEditData.data.enableEditFacet);
	const activeRoofTool = useSelector((state: RootState) => state.roofToolLines.data.activeRoofTool);
	const allRoofEdges = useSelector(selectAllEdges);

	const transformedEdgeLengths = useMemo(() => (
		allRoofEdges.map((edge: EdgeData) => {

			const polygon = allRoofSegs.find(r => r.id === edge.roofSegmentId);

			const a = getPolygonArea(polygon?.simplifiedHullCoords?.flat() || []);
			const isCCW = a > 0;
			const baseOffset = !fullScreenEnabled
				? (imgHeight <= 601 ? 3 : 2)
				: (imgHeight <= 601 ? 2.5 : 2);
			const offsetInside = baseOffset * (1 / Math.sqrt(scale.x));

			const [[x1, y1], [x2, y2]] = edge.coords;
			const midX = (x1 + x2) / 2;
			const midY = (y1 + y2) / 2;

			// eslint-disable-next-line prefer-const
			let angleDeg = Math.atan2(y2 - y1, x2 - x1) * 180 / Math.PI;

			const dx = x2 - x1;
			const dy = y2 - y1;
			const length = Math.sqrt(dx * dx + dy * dy);

			// Interior normal
			let nx, ny;
			if (!isCCW) {
				nx = dy / length;
				ny = -dx / length;
			} else {
				nx = -dy / length;
				ny = dx / length;
			}

			// Flip upside-down labels
			if (angleDeg > 90 || angleDeg < -90) {
				angleDeg += 180;
			}

			// Move inside along the interior normal
			const posX = midX + nx * offsetInside;
			const posY = midY + ny * offsetInside;

			return ({
				id: edge.coordId,
				x: posX,
				y: posY,
				angle: angleDeg,
				text: convertMetricToLinearFeet(edge.length),
				pxLength: (edge.length / groundMetersPerPixel) * scale.x,
			});
		})), [allRoofEdges, allRoofSegs, fullScreenEnabled, groundMetersPerPixel, imgHeight, scale.x]);

	return (
		<Group
			visible={!enableEditFacet && !activeRoofTool && !threeDModelEnabled }
			scale={scale}
			listening={false}
			name={KonvaGroupNames.EDGE_LENGHT}
		>
			{transformedEdgeLengths.map((edge) => (
				<EdgeLengthText key={edge.id} edge={edge} fullScreenEnabled={fullScreenEnabled} imgHeight={imgHeight} groundMetersPerPixel={groundMetersPerPixel} />
			))}
		</Group>
	);
};

const EdgeLength = memo(() => {

	const designType = useSelector((state: RootState) => state.roofData.data.designType);
	if (designType === 'ROOF')
		return <EdgeLengthRoof />;
	return <EdgeLengthSolar />;

});

EdgeLength.displayName = 'EdgeLength';
export default EdgeLength;

function EdgeLengthText({ edge, fullScreenEnabled, imgHeight, groundMetersPerPixel }: {
	edge: Konva.TextConfig & { pxLength: number; },
	fullScreenEnabled: boolean,
	imgHeight: number,
	groundMetersPerPixel: number;
}) {
	const { konvaRef } = useRefs();
	const screenSize = useScreenDims();
	const absoluteScale = konvaRef?.current?.getAbsoluteScale() || { x: 1, y: 1 };
	const textRef = useRef<Konva.Text | null>(null);
	const baseFontSize = !fullScreenEnabled
		? (imgHeight <= 601 ? 7 : screenSize.width < 935 ? 10 : 8)
		: (imgHeight <= 601 ? 15 : 20);
	const zoomScale = absoluteScale.x;
	const fontSize = Math.max(baseFontSize / (zoomScale), 2);
	const scaledLength = (edge.pxLength) * zoomScale;
	const visibleLengthThreshold =
		(fullScreenEnabled ? 6 : 4) / groundMetersPerPixel;
	const visibleLengthCondition = scaledLength > visibleLengthThreshold;

	const visiblity = edge.visible === undefined ? visibleLengthCondition : visibleLengthCondition && edge.visible;

	return (
		<Text
			ref={textRef}
			scaleY={1}
			scaleX={1}
			visible={visiblity}
			text={`${edge.text}`}
			x={edge.x}
			y={edge.y}
			fontSize={fontSize}
			strokeScaleEnabled={false}
			listening={false}
			fill="white"
			fontStyle="550"
			rotation={edge.angle}
			align="center"
			verticalAlign="middle"
			offsetX={textRef.current?.width() ? textRef.current.width() / 2 : 0}
			offsetY={textRef.current?.height() ? textRef.current.height() / 2 : 0}
			name={edge.name || ''}
			attrs={{
				visibleLengthCondition,
				pxLength: edge.pxLength,
			}}
		/>
	);
}

// const MemoEdgeLengthText = memo(EdgeLengthText, (prev, next) => {
// 	const a = prev.edge;
// 	const b = next.edge;
// 	return (
// 		a.id === b.id &&
//     a.x === b.x &&
//     a.y === b.y &&
//     a.angle === b.angle &&
//     a.text === b.text &&
//     a.pxLength === b.pxLength &&
//     prev.fullScreenEnabled === next.fullScreenEnabled &&
//     prev.imgHeight === next.imgHeight &&
//     prev.groundMetersPerPixel === next.groundMetersPerPixel
// 	);
// });